import * as XLSX from "xlsx";
import JSZip from "jszip";
import mammoth from "mammoth/mammoth.browser";
import { studioApi } from "./documentStudioApi";

const escapeHtml = (text) => String(text).replace(/[&<>]/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[char]));

const docxXmlText = (xml) => {
  const parsed = new DOMParser().parseFromString(xml, "application/xml");
  return [...parsed.getElementsByTagNameNS("*", "p")].map((paragraph) => {
    const text = String(paragraph.textContent || "").replace(/\s+/g, " ").trim();
    return text ? `<p>${escapeHtml(text)}</p>` : "";
  }).filter(Boolean).join("");
};

const extractDocxHeaderFooterHtml = async (buffer) => {
  const zip = await JSZip.loadAsync(buffer);
  const parts = Object.keys(zip.files).filter((name) => /^word\/(header|footer)\d+\.xml$/i.test(name)).sort();
  const headers = [];
  const footers = [];
  for (const name of parts) {
    const html = docxXmlText(await zip.file(name).async("string"));
    if (/\/header\d+\.xml$/i.test(name)) headers.push(html);
    else footers.push(html);
  }
  return { header: headers.filter(Boolean).join(""), footer: footers.filter(Boolean).join("") };
};

const extractDocxHtml = async (buffer) => {
  const result = await mammoth.convertToHtml({ arrayBuffer: buffer }, {
    includeDefaultStyleMap: true,
    ignoreEmptyParagraphs: false,
    convertImage: mammoth.images.imgElement((image) => image.read("base64").then((base64) => ({ src: `data:${image.contentType};base64,${base64}`, alt: "Imported Word image" }))),
  });
  return String(result.value || "").replace(/\t/g, "\u0009").replace(/<table(?![^>]*class=)/gi, '<table class="wv-imported-table"').replace(/<script[\s\S]*?<\/script>/gi, "").replace(/\son[a-z]+\s*=\s*(['"]).*?\1/gi, "");
};

export const studioKindForName = (name = "") => {
  const ext = String(name).split(".").pop()?.toLowerCase();
  if (["xlsx", "xls", "csv"].includes(ext)) return "spreadsheet";
  if (ext === "pdf") return "pdf";
  if (["docx", "txt"].includes(ext)) return "word";
  return "";
};

export const studioRouteFor = (kind, id) => `/studio/${kind === "spreadsheet" ? "sheet" : kind === "pdf" ? "pdf" : "word"}/${id}`;

export const importBlobToStudio = async ({ blob, fileName, documentType = "", classification = "Restricted" }) => {
  const kind = studioKindForName(fileName);
  if (!kind) throw new Error("This file type is not supported by Document Studio.");
  const title = String(fileName || "Imported document").replace(/\.[^.]+$/, "");
  const file = new File([blob], fileName || `document.${kind === "spreadsheet" ? "xlsx" : kind === "pdf" ? "pdf" : "docx"}`, { type: blob.type });
  if (kind === "pdf") {
    const data = await studioApi.importPdf(file, { title, documentType, classification });
    return { id: data.document._id, kind };
  }

  const buffer = await blob.arrayBuffer();
  let content = "";
  if (kind === "spreadsheet") {
    const workbook = XLSX.read(buffer, { type: "array" });
    const sheets = workbook.SheetNames.map((name) => {
      const sheet = workbook.Sheets[name];
      const range = XLSX.utils.decode_range(sheet["!ref"] || "A1:A1");
      const cells = {};
      for (let row = range.s.r; row <= Math.min(range.e.r, 999); row += 1) {
        for (let column = range.s.c; column <= Math.min(range.e.c, 99); column += 1) {
          const address = XLSX.utils.encode_cell({ r: row, c: column });
          const cell = sheet[address];
          if (!cell) continue;
          const value = cell.f ? `=${cell.f}` : cell.v != null ? String(cell.v) : "";
          if (value !== "") cells[address] = { v: value, f: cell.f ? `=${cell.f}` : undefined };
        }
      }
      return { name, cells };
    });
    content = JSON.stringify({ sheets: sheets.length ? sheets : [{ name: "Sheet1", cells: {} }] });
  } else if (/\.txt$/i.test(fileName)) {
    const text = new TextDecoder().decode(buffer);
    content = text.split(/\n{2,}/).map((block) => `<p>${escapeHtml(block).replace(/\n/g, "<br/>")}</p>`).join("");
  } else {
    content = await extractDocxHtml(buffer);
    const sections = await extractDocxHeaderFooterHtml(buffer);
    content = `${sections.header ? `<div class="wv-imported-header">${sections.header}</div>` : ""}${content}${sections.footer ? `<div class="wv-imported-footer">${sections.footer}</div>` : ""}`;
  }
  if (!content.trim()) content = `<p>Imported file: ${escapeHtml(fileName)}</p>`;
  const data = await studioApi.importDocument({ kind, title, content, documentType, classification });
  return { id: data.document._id, kind };
};
